import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Subtitle1,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { packageFileOf, type DataPackage, type PackageId } from '@lg/core';
import { useApi } from '../api/client';
import { errorMessage, saveFile } from '../ui/format';
import { D365_WORKFLOW_TEMPLATES, fetchD365WorkflowFile } from '../wizard/d365Workflows';
import { TemplateExportCard } from './TemplateExportCard';

/**
 * Columns D365 needs but a reader doesn't: fixed values (network domain, language) and the
 * alias, which repeats the email. Hidden in the preview only; the package keeps them.
 */
const HIDDEN_COLUMNS = new Set(['ALIAS', 'NETWORKDOMAIN', 'USERINFO_LANGUAGE', 'HELPLANGUAGE']);

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  card: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  head: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  spacer: { flex: 1 },
  wrap: { overflowX: 'auto', maxHeight: '320px', overflowY: 'auto' },
  // Columns sized to their content instead of equal widths, so values never overlap.
  grid: { tableLayout: 'auto', width: 'auto', minWidth: '100%' },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, whiteSpace: 'nowrap' },
  entityHead: { display: 'inline-flex', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  steps: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
});

/** The legal name the setup package gives the new company. */
function legalNameOf(setup: DataPackage): string {
  const entity = setup.entities.find((e) => e.key === 'legalEntities');
  const i = entity?.columns.indexOf('NAME') ?? -1;
  return (i >= 0 ? entity?.rows[0]?.[i] : undefined) ?? '';
}

export function DataPackageTab({ requestId }: { requestId: string }) {
  const styles = useStyles();
  const api = useApi();
  const pkgs = useQuery({ queryKey: ['packages', requestId], queryFn: () => api.getDataPackages(requestId) });

  if (pkgs.isPending) return <Spinner label="Building packages…" />;
  if (pkgs.isError)
    return (
      <MessageBar intent="error">
        <MessageBarBody>{errorMessage(pkgs.error)}</MessageBarBody>
      </MessageBar>
    );
  const { setup, people, projects, ledger, companyId } = pkgs.data;

  return (
    <div className={styles.stack}>
      <MessageBar intent="info" layout="multiline">
        <MessageBarBody>
          <MessageBarTitle>Data management packages</MessageBarTitle>
          Standard D365 packages (Manifest.xml, PackageHeader.xml and one CSV per entity), built from this
          request. Import them by hand in Data management, or let provisioning send them to the recurring data
          job. It is the same file either way.
        </MessageBarBody>
      </MessageBar>
      <PackageCard requestId={requestId} pkg={setup} which="setup" />
      {people ? (
        <PackageCard requestId={requestId} pkg={people} which="people" />
      ) : (
        <Card className={styles.card}>
          <Body1>Load people and approvals data to build the people package.</Body1>
        </Card>
      )}
      {projects && <PackageCard requestId={requestId} pkg={projects} which="projects" />}
      <PackageCard requestId={requestId} pkg={ledger} which="ledger" />
      <D365WorkflowCard companyId={companyId} />
      <TemplateExportCard companyId={companyId} legalName={legalNameOf(setup)} />
      <Card className={styles.card}>
        <Subtitle1 as="h2">Getting them into D365</Subtitle1>
        <Body1>
          <b>By hand</b> (good for showing the data land):
        </Body1>
        <ol className={styles.steps}>
          <li>
            Data management → Import → name it, set Source data format to <b>Package</b>, upload the zip.
          </li>
          <li>
            Run the <b>{setup.definitionGroup}</b> package first (from company DAT), then the template copy
            into {companyId}.
          </li>
          <li>
            Switch to company <b>{companyId}</b> and import{' '}
            <b>{people?.definitionGroup ?? 'LG-VENUE-PEOPLE'}</b>
            {projects && (
              <>
                , then <b>{projects.definitionGroup}</b> (provisioning creates the same records over OData)
              </>
            )}
            .
          </li>
        </ol>
        <Body1>
          <b>Automated</b> (recurring data job):
        </Body1>
        <ol className={styles.steps}>
          <li>
            Create data projects {setup.definitionGroup} and {people?.definitionGroup ?? 'LG-VENUE-PEOPLE'}{' '}
            with these entities, in this order.
          </li>
          <li>
            Create a recurring data job for each (Data management → Data project → Create recurring data job),
            authorised for the app's Entra application.
          </li>
          <li>
            Provisioning enqueues the zip to{' '}
            <span className={styles.mono}>
              /api/connector/enqueue/&lt;activity ID&gt;?company={companyId}
            </span>{' '}
            and tracks the message status.
          </li>
        </ol>
        <Caption1>
          Entity names, columns and the manifest layout are best-guess (VERIFY). Export each data project once
          from the sandbox and the export replaces them.
        </Caption1>
      </Card>
    </div>
  );
}

/**
 * The working D365 journal workflow export from the template company, saved for this company
 * (same file as the Workflows step). Imported by hand after the ledger package.
 */
function D365WorkflowCard({ companyId }: { companyId: string }) {
  const styles = useStyles();
  const download = useMutation({
    mutationFn: async () => saveFile(await fetchD365WorkflowFile('Journal', companyId)),
  });
  const t = D365_WORKFLOW_TEMPLATES['Journal'];
  if (!t) return null;
  return (
    <Card className={styles.card}>
      <div className={styles.head}>
        <Subtitle1 as="h2">General journal approval workflow</Subtitle1>
        <Badge appearance="tint" color="informative">
          company {companyId}
        </Badge>
        <span className={styles.spacer} />
        <Button appearance="primary" disabled={download.isPending} onClick={() => download.mutate()}>
          Download D365 workflow (.xml)
        </Button>
      </div>
      <Body1>
        {t.file} from {t.from}, with only the company changed to {companyId}. Import the ledger package first:
        the workflow's due dates use work calendar <b>{t.calendar}</b>, which it creates in {companyId}
        (without it the import reports &quot;The calendar token does not exist&quot;). Then import this file
        in {companyId} with General ledger → Journal setup → General ledger workflows → Import, select it on
        journal name WFGenJrn and switch Approval workflow on.
      </Body1>
      {download.isError && (
        <MessageBar intent="error">
          <MessageBarBody>{errorMessage(download.error)}</MessageBarBody>
        </MessageBar>
      )}
    </Card>
  );
}

function PackageCard({ requestId, pkg, which }: { requestId: string; pkg: DataPackage; which: PackageId }) {
  const styles = useStyles();
  const api = useApi();
  const download = useMutation({
    mutationFn: async () => saveFile(await api.downloadDataPackage(requestId, which)),
  });
  const total = pkg.entities.reduce((n, e) => n + e.rows.length, 0);
  return (
    <Card className={styles.card}>
      <div className={styles.head}>
        <Subtitle1 as="h2">{pkg.title}</Subtitle1>
        <Badge appearance="outline">{pkg.definitionGroup}</Badge>
        <Badge appearance="tint" color="informative">
          company {pkg.companyContext}
        </Badge>
        <span className={styles.spacer} />
        <Button
          appearance="primary"
          disabled={download.isPending || total === 0}
          onClick={() => download.mutate()}
        >
          Download package (.zip)
        </Button>
      </div>
      <Body1>
        {pkg.description} {pkg.entities.length} entities, {total} records.
      </Body1>
      {download.isError && (
        <MessageBar intent="error">
          <MessageBarBody>{errorMessage(download.error)}</MessageBarBody>
        </MessageBar>
      )}
      <Accordion multiple collapsible>
        {pkg.entities.map((e) => {
          const shown = e.columns.flatMap((c, i) => (HIDDEN_COLUMNS.has(c) ? [] : [i]));
          const hidden = e.columns.filter((c) => HIDDEN_COLUMNS.has(c));
          return (
            <AccordionItem key={e.entityName} value={e.entityName}>
              <AccordionHeader>
                <span className={styles.entityHead}>
                  <Caption1>{e.sequence}.</Caption1> <b>{e.entityName}</b>
                  <Badge size="small" appearance="tint">
                    {e.rows.length} rows
                  </Badge>
                  <Caption1 className={styles.mono}>{packageFileOf(e)}</Caption1>
                </span>
              </AccordionHeader>
              <AccordionPanel>
                <div className={styles.wrap}>
                  <Table size="extra-small" aria-label={e.entityName} className={styles.grid}>
                    <TableHeader>
                      <TableRow>
                        {shown.map((i) => (
                          <TableHeaderCell key={e.columns[i]} className={styles.mono}>
                            {e.columns[i]}
                          </TableHeaderCell>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {e.rows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={shown.length}>No rows</TableCell>
                        </TableRow>
                      )}
                      {e.rows.map((r, i) => (
                        <TableRow key={i}>
                          {shown.map((j) => (
                            <TableCell key={j} className={styles.mono}>
                              {r[j]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {hidden.length > 0 && (
                  <Caption1>Also in the file, with fixed values: {hidden.join(', ')}.</Caption1>
                )}
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
      {pkg.notes.map((n) => (
        <Caption1 key={n}>{n}</Caption1>
      ))}
    </Card>
  );
}
